import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface TestPlanRow {
  readonly testPlanId: string;
  readonly planCode: string;
  readonly title: string;
  readonly releaseRef: string | null;
  readonly status: string;
  readonly approvalStatus: string;
}

const STATUSES = ['', 'DRAFT', 'IN_REVIEW', 'APPROVED', 'ACTIVE', 'CLOSED', 'CANCELLED'];
const APPROVALS = ['', 'PENDING', 'APPROVED', 'REJECTED'];

/**
 * M34 — Test Plan Repository (§31 / §7). Register of master test plans (T448)
 * with the draft→review→approved→active→closed lifecycle and the segregated
 * approval workflow. Supports authoring a plan and driving it — submit, approve,
 * reject, activate, close and delete — each wired to the guarded
 * /v1/qms/test-plans write API. Filterable by plan status and approval status.
 */
export function TestPlanRepository(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<TestPlanRow[]>([]);
  const [status, setStatus] = useState('');
  const [approvalStatus, setApprovalStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newTitle, setNewTitle] = useState('');
  const [newRelease, setNewRelease] = useState('');
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (approvalStatus) params.set('approvalStatus', approvalStatus);
    const qs = params.toString();
    apiRequest<{ data: TestPlanRow[] }>(`/v1/qms/test-plans${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, approvalStatus, reload]);

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m34.common.notAuthorised', { perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newTitle.trim()) { setActionError(t('m34.plan.validation')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/qms/test-plans`, {
        method: 'POST',
        body: { title: newTitle.trim(), releaseRef: newRelease.trim() || undefined },
        token,
      });
      setNotice(t('m34.plan.created', { title: newTitle.trim() }));
      setNewTitle('');
      setNewRelease('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'M34_TEST_PLAN_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: TestPlanRow, path: string, label: string, perm: string, body?: Record<string, unknown>): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/qms/test-plans/${r.testPlanId}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(t('m34.plan.notice', { code: r.planCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, perm));
    }
  };

  const remove = async (r: TestPlanRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/qms/test-plans/${r.testPlanId}`, { method: 'DELETE', token });
      setNotice(t('m34.plan.notice', { code: r.planCode, label: t('m34.plan.act.deleted') }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'M34_TEST_PLAN_MANAGE'));
    }
  };

  return (
    <main aria-label="test-plan-repository">
      <h1>{t('m34.plan.title')}</h1>

      <section aria-label="create-test-plan">
        <h2>{t('m34.plan.createHeading')}</h2>
        <form aria-label="test-plan-form" onSubmit={(e) => void create(e)}>
          <label>{t('m34.common.title')}<input aria-label="field-plan-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} /></label>
          <label>{t('m34.common.release')}<input aria-label="field-plan-release" value={newRelease} onChange={(e) => setNewRelease(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-plan" disabled={busy}>{busy ? t('m34.plan.creating') : t('m34.plan.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="plan-action-error">{actionError}</p>}
        {notice && <p aria-label="plan-action-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m34.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m34.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m34.plan.approval')}
          <select aria-label="filter-approval" value={approvalStatus} onChange={(e) => setApprovalStatus(e.target.value)}>
            {APPROVALS.map((s) => <option key={s || 'all'} value={s}>{s || t('m34.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m34.common.loading')}</p> : (
        <>
          <p aria-label="plan-count">{t('m34.plan.count', { n: rows.length })}</p>
          <DataTable
            ariaLabel="plan-table"
            rows={rows}
            rowKey={(r) => r.testPlanId}
            columns={[
              { key: 'planCode', header: t('m34.common.code'), render: (r) => r.planCode },
              { key: 'title', header: t('m34.common.title'), render: (r) => r.title },
              { key: 'releaseRef', header: t('m34.common.release'), render: (r) => r.releaseRef ?? '—' },
              { key: 'status', header: t('m34.common.status'), render: (r) => r.status },
              { key: 'approvalStatus', header: t('m34.plan.approval'), render: (r) => r.approvalStatus },
              { key: 'actions', header: t('m34.common.actions'), render: (r) => (
                <>
                  {r.status === 'DRAFT' && (
                    <Button variant="secondary" type="button" aria-label={`submit-${r.planCode}`} onClick={() => void act(r, 'submit', t('m34.plan.act.submitted'), 'M34_TEST_PLAN_MANAGE')}>{t('m34.plan.btnSubmit')}</Button>
                  )}
                  {r.status === 'IN_REVIEW' && (
                    <>
                      <Button variant="secondary" type="button" aria-label={`approve-${r.planCode}`} onClick={() => void act(r, 'approve', t('m34.plan.act.approved'), 'M34_TEST_PLAN_APPROVE')}>{t('m34.plan.btnApprove')}</Button>
                      <input aria-label={`reason-${r.planCode}`} value={reasons[r.testPlanId] ?? ''} onChange={(e) => setReasons((m) => ({ ...m, [r.testPlanId]: e.target.value }))} placeholder={t('m34.plan.rejectReason')} />
                      <Button variant="danger" type="button" aria-label={`reject-${r.planCode}`} onClick={() => void act(r, 'reject', t('m34.plan.act.rejected'), 'M34_TEST_PLAN_APPROVE', { reason: reasons[r.testPlanId] ?? '' })}>{t('m34.plan.btnReject')}</Button>
                    </>
                  )}
                  {r.status === 'APPROVED' && (
                    <Button variant="secondary" type="button" aria-label={`activate-${r.planCode}`} onClick={() => void act(r, 'activate', t('m34.plan.act.activated'), 'M34_TEST_PLAN_MANAGE')}>{t('m34.plan.btnActivate')}</Button>
                  )}
                  {r.status === 'ACTIVE' && (
                    <Button variant="secondary" type="button" aria-label={`close-${r.planCode}`} onClick={() => void act(r, 'close', t('m34.plan.act.closed'), 'M34_TEST_PLAN_MANAGE')}>{t('m34.plan.btnClose')}</Button>
                  )}
                  {!['CLOSED', 'CANCELLED'].includes(r.status) && (
                    <Button variant="danger" type="button" aria-label={`delete-${r.planCode}`} onClick={() => void remove(r)}>{t('m34.common.delete')}</Button>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
